<template>
  <li>
    <label class="group relative block cursor-pointer">
      <input
        :checked="option.selected"
        class="absolute inset-0 opacity-0"
        :disabled="shouldDisplayDisabledState"
        :name="`step-${radioName || step.id}`"
        type="radio"
        :value="option.trackingValue"
        @keydown.enter="onClick"
      />
      <LargePicker
        :addon="option.addon"
        :best-deal
        :color="option.color"
        :contextual-callout
        :data-qa="dataQaAttribute"
        :disabled="shouldDisplayDisabledState"
        :hide-sold-out
        :illustration="option.icon"
        :index
        :is-good-deal="option.goodDeal"
        :is-small
        :label
        :label-highlight="option.highlight"
        :loading
        :prefix="option.style?.prefix"
        :price="description"
        :selected="option.selected"
        :sub-label
        :tag
        @click="onClick"
      />
    </label>
  </li>
</template>

<script lang="ts" setup>
import { navigateTo } from '#imports'
import { computed, onMounted, ref, watch } from 'vue'

import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '~/scopes/buyback/constants'
import { useCreateLinkFromPicker } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/composables/useCreateLinkFromPicker/useCreateLinkFromPicker'

import type { Step } from '../../utils/types'
import LargePicker from '../LargePicker/LargePicker.vue'

import translations from './StepOption.translations'

const { trackClick } = useTracking()
const i18n = useI18n()
const { createLink } = useCreateLinkFromPicker()

const { deleteBuybackOffer } = useBuybackOffer()
const loading = ref(false)
const pendingClick = ref(false)

const props = withDefaults(
  defineProps<{
    step: Step
    option: Step['options'][number]
    index: number
    isGroupLoading?: boolean
    isSmall?: boolean
    hideSoldOut?: boolean
    radioName?: string
    firstPickerId?: string
  }>(),
  {
    isGroupLoading: false,
    isSmall: false,
    hideSoldOut: false,
    radioName: '',
    firstPickerId: '',
  },
)

const emit = defineEmits<{
  click: [Step['options'][number], Step]
  changed: [Step['options'][number], Step]
}>()

const experiments = useExperiments()

// When we click on a picker, the `loading` state is added manually. Behind the scene, we're triggering
// a /pickers endpoint call, so let's remove the loading state once the picker status change to selected
// (as it's backend driven)
watch(
  () => props.option.selected,
  () => {
    if ((loading.value || pendingClick.value) && props.option.selected) {
      emit('changed', props.option, props.step)
      loading.value = false
      pendingClick.value = false
    }
  },
)
// Important to always clear the loading state
onMounted(() => {
  loading.value = false
  pendingClick.value = false
})

const description = computed(() => {
  if (props.step.id === 'battery' && !props.option.acquirable) {
    return ''
  }

  if (props.step.id === 'battery' && !props.option.available) {
    return i18n(translations.comingSoon)
  }

  if (
    !props.option.available &&
    props.step.id !== 'mobile_plan' &&
    !props.hideSoldOut
  ) {
    return i18n(translations.soldOut)
  }

  if (!props.option.price) {
    return ''
  }

  // For trade-in, the price is an already formatted string :'(
  if (typeof props.option.price === 'string') {
    return props.option.price
  }

  return i18n.price(props.option.price)
})

const label = computed(() => {
  return typeof props.option.label === 'string'
    ? props.option.label
    : i18n(props.option.label)
})

const subLabel = computed(() => {
  return typeof props.option.subLabel === 'object'
    ? i18n(props.option.subLabel)
    : props.option.subLabel
})

const tag = computed(() => {
  if (
    props.option.tag &&
    // FIXME: remove this condition and add the check
    // in createCustomizationFunnelData
    (props.step.id === 'mobile_plan' ||
      experiments['experiment.ppUpgradeForLessV2'] !== 'withUpgrade')
  ) {
    return {
      label: props.option.tag.label,
      variant: props.option.tag.type,
    }
  }

  return ''
})

const shouldDisplayDisabledState = computed(() => {
  return !props.option.available || (!loading.value && props.isGroupLoading)
})

const bestDeal = computed(() => {
  return {
    shouldDisplay:
      props.step.id === 'grades' &&
      props.option.bestDeal &&
      experiments['experiment.ppPriceOpportunity'] === 'withPriceOpportunity',
    title: i18n(translations.bestDealTitle),
    description: i18n(translations.bestDealGradesDescription),
  }
})

const contextualCallout = computed(() => {
  if (!props.option.selected) return null

  return props.option.contextualCallout ?? null
})

const dataQaAttribute = computed(() => {
  const id = String(props.option.trackingValue || label.value)
    .replace(/\s+/g, '_')
    .toLowerCase()

  return props.step.id === 'trade_in' && props.option.trackingValue === 'yes'
    ? 'swap-cta'
    : `picker-option-${props.index}-${id}`
})

async function onClick() {
  if (!props.option.available || loading.value || props.isGroupLoading) {
    return false
  }

  if (props.step.id === 'battery') {
    // We have an exception on new battery for now, to be compatible with the legacy event we have on the
    // toggle in the previous pickers.
    trackClick({
      zone: props.step.trackingId ?? '',
      name: props.step.trackingId ?? 'unknown_picker',
      value: props.option.trackingValue ?? '',
    })
  } else {
    // We're a bit defensive on those trackingId / trackingValue, as the API specs says that
    // both fields are optional. Let's have default values to make sure we're able to detect this
    // in amplitude no matter what
    trackClick({
      zone: props.step.trackingId ?? 'unknown_picker',
      name: props.option.trackingValue ?? label.value,
    })
  }

  // // Custom work for the trade in step as we don't refresh anything from the pickers endpoint
  if (props.step.id === 'trade_in') {
    // Let's open the modal by clicking Yes
    if (props.option.trackingValue === 'yes') {
      openModal(MODAL_NAMES.SWAP_MIX_AND_MATCH)
    }
    // Remove the offer from the cart if the users selects no after accepting a swap offer
    if (props.option.trackingValue === 'no' && !props.option.selected) {
      void deleteBuybackOffer()
    }

    return false
  }

  emit('click', props.option, props.step)

  // TODO: refactor this with StepKeyboard to remove duplication on tracking and redirection
  if (props.step.id === 'keyboard_type_language') {
    return false
  }

  // If the field is already selected, we won't change the URL nor refresh the network calls, so
  // let's directly trigger the changed event and skip the loading state
  if (props.option.selected) {
    emit('changed', props.option, props.step)
  } else {
    loading.value = true
  }

  // Update current URL
  const link = createLink(props.option, props.firstPickerId, props.step)
  if (link) {
    await navigateTo(link)
  }

  return true
}
</script>
